import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';
import { Plus, Paperclip, X, Trash2 } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/Card';
import Modal from '@/components/Modal';
import { StatusBadge } from '@/components/Badge';
import Loader, { EmptyState } from '@/components/Loader';
import Pagination from '@/components/Pagination';
import { workReportService, projectService, taskService, fileUrl } from '@/services';
import { fmtDate } from '@/utils/format';

export default function WorkReports() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [showNew, setShowNew] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['work-reports', 'mine', page],
    queryFn: () => workReportService.mine({ page, limit: 10 }),
  });

  const remove = async (id) => {
    try {
      await workReportService.remove(id);
      toast.success('Deleted');
      qc.invalidateQueries({ queryKey: ['work-reports'] });
    } catch (e) {
      toast.error('Failed');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Daily Work"
        subtitle="Log what you worked on today and update task progress"
        actions={
          <button className="btn-primary" onClick={() => setShowNew(true)}>
            <Plus size={16} /> Log work
          </button>
        }
      />

      {isLoading ? (
        <Loader />
      ) : (data?.items || []).length === 0 ? (
        <EmptyState title="No work logs yet" subtitle="Click 'Log work' to add your first daily update" />
      ) : (
        <Card>
          <div className="space-y-3">
            {data.items.map((r) => (
              <div key={r._id} className="border border-slate-200 dark:border-slate-800 rounded-xl p-4 hover:bg-slate-50 dark:hover:bg-slate-800/30">
                <div className="flex flex-wrap justify-between gap-3 mb-2">
                  <div>
                    <div className="font-semibold">{r.title}</div>
                    <div className="text-xs text-slate-500">
                      {fmtDate(r.date)} · {r.project?.name || 'No project'} {r.task?.title ? `· ${r.task.title}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={r.status} />
                    <span className="text-xs text-slate-500">{r.hoursWorked || 0}h · {r.completionPercent || 0}%</span>
                    <button onClick={() => remove(r._id)} className="btn-ghost text-rose-600 p-1">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{r.description}</div>
                {(r.attachments || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {r.attachments.map((a, i) => (
                      <a key={i} href={fileUrl(a.url)} target="_blank" rel="noreferrer" className="text-xs text-brand-600 underline">
                        {a.filename}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <Pagination page={page} limit={10} total={data?.total || 0} onChange={setPage} />
        </Card>
      )}

      <NewReportModal
        open={showNew}
        onClose={() => setShowNew(false)}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ['work-reports'] });
          setShowNew(false);
        }}
      />
    </div>
  );
}

function NewReportModal({ open, onClose, onCreated }) {
  const { register, handleSubmit, reset, watch, formState: { isSubmitting } } = useForm({
    defaultValues: { date: dayjs().format('YYYY-MM-DD'), status: 'in-progress', hoursWorked: 1, completionPercent: 0 },
  });
  const [files, setFiles] = useState([]);
  const projectId = watch('project');

  const projects = useQuery({ queryKey: ['projects', 'all'], queryFn: () => projectService.list({ limit: 100 }), enabled: open });
  const tasks = useQuery({
    queryKey: ['tasks', 'by-project', projectId, 'mine'],
    queryFn: () => taskService.list({ project: projectId, mine: true, limit: 100 }),
    enabled: open && !!projectId,
  });

  const onSubmit = async (data) => {
    try {
      const fd = new FormData();
      Object.entries(data).forEach(([k, v]) => {
        if (v !== '' && v !== undefined && v !== null) fd.append(k, v);
      });
      files.forEach((f) => fd.append('attachments', f));
      await workReportService.create(fd);
      toast.success('Work logged');
      reset();
      setFiles([]);
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Log daily work" size="lg" footer={
      <>
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button form="workForm" type="submit" className="btn-primary" disabled={isSubmitting}>Save</button>
      </>
    }>
      <form id="workForm" onSubmit={handleSubmit(onSubmit)} className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="label">Date</label>
          <input type="date" className="input" {...register('date', { required: true })} />
        </div>
        <div>
          <label className="label">Status</label>
          <select className="input" {...register('status')}>
            <option value="pending">Pending</option>
            <option value="in-progress">In progress</option>
            <option value="completed">Completed</option>
          </select>
        </div>
        <div>
          <label className="label">Project</label>
          <select className="input" {...register('project')}>
            <option value="">— None —</option>
            {(projects.data?.items || []).map((p) => (
              <option key={p._id} value={p._id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Task</label>
          <select className="input" {...register('task')}>
            <option value="">— None —</option>
            {(tasks.data?.items || []).map((t) => (
              <option key={t._id} value={t._id}>{t.title}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="label">Title</label>
          <input className="input" {...register('title', { required: true })} placeholder="What did you work on?" />
        </div>
        <div className="sm:col-span-2">
          <label className="label">Description</label>
          <textarea rows={4} className="input" {...register('description', { required: true })} />
        </div>
        <div>
          <label className="label">Hours worked</label>
          <input type="number" step="0.5" min={0} className="input" {...register('hoursWorked')} />
        </div>
        <div>
          <label className="label">Completion %</label>
          <input type="number" min={0} max={100} className="input" {...register('completionPercent')} />
        </div>
        <div className="sm:col-span-2">
          <label className="label">Attachments</label>
          <div className="flex flex-wrap items-center gap-2">
            <label className="btn-secondary cursor-pointer">
              <Paperclip size={14} /> Attach files
              <input type="file" multiple className="hidden" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
            </label>
            {files.map((f, i) => (
              <span key={i} className="text-xs px-2 py-1 rounded bg-slate-100 dark:bg-slate-800 flex items-center gap-1">
                {f.name}
                <button type="button" onClick={() => setFiles((arr) => arr.filter((_, j) => j !== i))}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>
      </form>
    </Modal>
  );
}
